import { useInfiniteQuery } from '@tanstack/react-query';
import { getFemaleRecommendList } from 'api';
import {
  Loading,
  Text,
  ConcatText,
  HorizontalVirtualList,
} from 'components';
import { useCallback, useMemo } from 'react';
import {
  FEMALE_CURRENT_POINT_PAGE_SIZE,
  PICKUPS_SCROLL_CONTAINER_ID,
  queryKeys,
  routes,
  TEXT_STRING,
  YARIKANO_BANNER_INSERT_INDEX,
} from '../../constants';
import { useNavigate } from 'react-router-dom';
import { TFemale } from 'types';
import { useAppStore } from 'store';
import NoAvatar from '../../assets/images/avatar.svg';

const YARIKANO_FC_URL = process.env.REACT_APP_YARIKANO_FC_URL || '';
const YARIKANO_FC_IMAGE = process.env.REACT_APP_YARIKANO_FC_IMAGE || '';

function ListPickupsFemale() {
  // Hook
  const navigate = useNavigate();
  const { filterFemale } = useAppStore();

  // Query
  const {
    data: listFemale,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [queryKeys.FEMALE_RECOMMEND_LIST, filterFemale],
    queryFn: async ({ pageParam }) => {
      return getFemaleRecommendList({
        currentPage: pageParam,
        pageSize: FEMALE_CURRENT_POINT_PAGE_SIZE,
        filter: filterFemale,
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, _, currentPage) => {
      return lastPage?.totalPage > currentPage ? currentPage + 1 : undefined;
    },
  });

  const { items, hasFemales } = useMemo(() => {
    const females: TFemale[] =
      listFemale?.pages?.flatMap((p) => p.data ?? []) ?? [];
    if (!YARIKANO_FC_IMAGE || !YARIKANO_FC_URL)
      return { items: females, hasFemales: females.length > 0 };

    const insertAt = Math.min(YARIKANO_BANNER_INSERT_INDEX, females.length);
    return {
      items: [...females.slice(0, insertAt), null, ...females.slice(insertAt)],
      hasFemales: females.length > 0,
    };
  }, [listFemale?.pages]);

  // Render item for virtual list
  const renderItem = useCallback(
    (item: TFemale | null) => {
      if (!item) {
        return (
          <a
            href={YARIKANO_FC_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col cursor-pointer rounded-lg flex-shrink-0 h-full overflow-hidden border-solid"
            style={{ width: '100%' }}
          >
            <img
              src={YARIKANO_FC_IMAGE}
              alt=""
              className={`w-full rounded-lg ${hasFemales ? 'aspect-[0.9] h-full' : ''}`}
            />
          </a>
        );
      }

      const female = item;
      return (
        <div
          key={female.id}
          onClick={() => {
            navigate(
              `${routes.DETAIL_FEMALE.replace(':id', `${female.userId}`)}`,
            );
          }}
          className="flex flex-col pb-2 cursor-pointer justify-center items-center bg-primary-50 border border-solid border-primary rounded-lg flex-shrink-0 h-full"
          style={{ width: '100%' }}
        >
          <div className="w-full relative">
            <div
              className={`h-full w-full absolute top-0 left-0 rounded-t-lg border border-neutral-200 
            background-gradient bg-gradient-to-br from-neutral-200 via-white to-neutral-200`}
            ></div>

            <img
              src={female.avatar || NoAvatar}
              alt=""
              className="w-full aspect-[0.9] object-cover object-top rounded-t-lg z-[1] relative"
              onError={(e) => (e.currentTarget.src = NoAvatar)}
            />
          </div>

          <div className="flex items-center justify-center gap-2 w-full px-2">
            <ConcatText
              hyphen=""
              prefixText={female.nickname}
              className="font-bold max-w-[90%] text-xs mt-2"
            />
          </div>
        </div>
      );
    },
    [navigate, hasFemales],
  );

  // Render loading item
  const renderLoading = useCallback(() => {
    return (
      <div className="flex flex-col pb-2 justify-center items-center bg-primary-50 border border-solid border-primary rounded-lg flex-shrink-0 h-full">
        <div className="flex-1 flex items-center justify-center">
          <Loading />
        </div>
      </div>
    );
  }, []);

  if (isLoading) {
    return (
      <div className="px-8px pt-4 mt-8">
        <Loading />
      </div>
    );
  }

  if (!items.length) {
    return null;
  }

  return (
    <div className="px-8px pt-4">
      <Text bold className="text-[12px] 2xs:text-[14px] xs:text-[16px]">
        {TEXT_STRING.SEARCH_PAGE.PICK_UP}
      </Text>
      <HorizontalVirtualList
        items={items}
        hasNext={false}
        isFetchingNext={isFetchingNextPage}
        onReachEnd={fetchNextPage}
        renderItem={renderItem}
        renderLoading={renderLoading}
        scrollableTarget={PICKUPS_SCROLL_CONTAINER_ID}
        itemWidth="25%"
        bufferSize={1}
        className='!py-2'
      />
    </div>
  );
}

export default ListPickupsFemale;
